import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import {
	ActionListItem,
	ActionListSubMenuHeader,
	Button,
	DropOverActionList,
	Icon,
	RichTextEditorToolbar,
	RichTextEditorToolbarFeature
} from "client/Components";
import {
	isNil,
	isNotNil,
	ensureNotNil
} from "client/Misc/utils";
import { CommunityInlineSelector } from "./CommunityInlineSelector";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./CommunityPostComposerFooter.module.less";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	MAX_IMAGES_PER_POST,
	CommunityPollFormData,
	CommunityPostImageFormData,
	PostComposerFormData,
	CommunityPostImageFormModelKind
} from "./common";
import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { Separator } from "./Separator";
import { RichTextEditorState } from "client/Components/RichTextEditor/types";
import {
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";

export type PostComposerSelectImageSourceKind = "device" | "workspace";

type CommunityPostComposerFooterProps = {
	attachable: IValueInputAttachable<PostComposerFormData>;
	communities: readonly CommunityViewModel[];
	hasPoll: boolean;
	onSubmit: () => void;
	initialCommunityId: number | null;
	isBusy: boolean;
	toolbar: ReactElement | null;
	onSelectImages: (source: PostComposerSelectImageSourceKind) => void;
	canAddLinkedDocuments: boolean;
	onAddLinkedDocument: (() => Promise<void>) | null;
};

export const CommunityPostComposerFooter = (
	props: CommunityPostComposerFooterProps
): ReactElement => {
	const {
		attachable,
		communities,
		initialCommunityId,
		hasPoll,
		onSubmit,
		isBusy,
		toolbar,
		onSelectImages,
		canAddLinkedDocuments,
		onAddLinkedDocument
	} = props;

	const attachableValue: PostComposerFormData = attachable.value;
	const images: readonly ObjectWithClientId<CommunityPostImageFormData>[] = attachableValue.images;

	const addPollHandler = useCallback(
		(): void => {
			attachable.for("poll")
				.reducer
				.setValue({
					isSet: true,
					value: getInitialPollData()
				});

			attachable.for("poll")
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const isEdit: boolean = isNotNil(
		attachableValue.id
	);

	const areImagesLoading: boolean = images.some(x => x.kind === CommunityPostImageFormModelKind.Loading);

	const isSaveButtonDisable: boolean = isBusy
		|| isNil(attachableValue.communityId)
		|| areImagesLoading;

	const canSelectMoreImages: boolean = images.length < MAX_IMAGES_PER_POST;

	const saveButtonDisabledTooltip: string | undefined = isNil(attachableValue.communityId)
		? localize("_SelectACommunityInOrderToCreateThisPost")
		: areImagesLoading
			? localize("_WaitUntilAllImagesAreUploadedBeforePosting")
			: undefined;

	return (
		<div>
			{(isEdit || isNil(initialCommunityId)) && (
				<>
					<Separator />
					<div className={styles.inlineSelectorContainer}>
						<CommunityInlineSelector
							attachable={attachable.for("communityId")}
							communities={communities}
							isReadOnly={isEdit}
						/>
					</div>
				</>
			)}
			<Separator />
			<div className={styles.toolbarAndSaveButton}>
				<div className={styles.richTextEditorToolbarContainer}>
					<div className={styles.toolbar}>
						<MoreToolbarActions
							canAddPoll={!hasPoll}
							onAddPoll={addPollHandler}
							canSelectImage={canSelectMoreImages}
							onSelectImages={onSelectImages}
							canAddLinkedDocuments={canAddLinkedDocuments}
							onAddLinkedDocument={onAddLinkedDocument}
						/>
						<div className={styles.toolbarDivider}/>
						{toolbar}
					</div>
				</div>

				<Button
					kind="primary"
					text={localize(isEdit ? "_Save" : "_Post")}
					trackingId="save"
					isDisabled={isSaveButtonDisable}
					tooltip={saveButtonDisabledTooltip}
					onClick={onSubmit}
				/>
			</div>
		</div>
	);
};
CommunityPostComposerFooter.displayName = "CommunityPostComposerFooter";

type CommunityPostComposerFooterToolbarProps = {
	editorState: RichTextEditorState;
	onChangeEditorState: (newValue: RichTextEditorState, isSignificant: boolean) => void;
	onFocusEditor: () => void;
};

const CommunityPostComposerFooterToolbar = (props: CommunityPostComposerFooterToolbarProps): ReactElement => {
	const {
		editorState,
		onChangeEditorState,
		onFocusEditor
	} = props;

	return (
		<RichTextEditorToolbar
			onChange={onChangeEditorState}
			value={editorState}
			isBorderless
			hasNoPadding
			supportedFeatures={RICH_TEXT_EDITOR_SUPPORTED_FEATURES}
			areHashUrlsAllowed
			onFocusInput={onFocusEditor}
		/>
	);
};
CommunityPostComposerFooterToolbar.displayName = "CommunityPostComposerFooter.Toolbar";

const RICH_TEXT_EDITOR_SUPPORTED_FEATURES: readonly RichTextEditorToolbarFeature[] = [
	"basic-tools",
	"font-color-tool",
	"eraser-tool",
	"list-tools",
	"link-tool",
	"emoji-tool"
];

type MoreToolbarActionsProps = {
	canSelectImage: boolean;
	canAddPoll: boolean;
	canAddLinkedDocuments: boolean;
	onSelectImages: (kind: PostComposerSelectImageSourceKind) => void;
	onAddPoll: () => void;
	onAddLinkedDocument: (() => Promise<void>) | null;
};

const MoreToolbarActions = (props: MoreToolbarActionsProps): ReactElement => {
	const {
		canSelectImage,
		canAddPoll,
		canAddLinkedDocuments,
		onSelectImages,
		onAddPoll,
		onAddLinkedDocument
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement | null>(null);

	return (
		<>
			<RichTextEditorToolbar.Button
				ref={ref}
				onClick={() => setIsOpen(x => !x)}
				handleOn="click"
				trackingId="more-actions"
				tooltip={localize("_Add")}
			>
				<Icon
					value="plus"
				/>
			</RichTextEditorToolbar.Button>
			{isOpen && (
				<DropOverActionList
					target={ensureNotNil(ref.current)}
					menuTitle={localize("_Add")}
					onClose={() => setIsOpen(false)}
					trackingId="more-actions-menu"
					targetAlignment="bl"
					contentAlignment="tl"
				>
					<DropOverContentView
						canSelectImage={canSelectImage}
						canAddPoll={canAddPoll}
						onSelectImages={onSelectImages}
						onAddPoll={onAddPoll}
						canAddLinkedDocuments={canAddLinkedDocuments}
						onAddLinkedDocument={onAddLinkedDocument}
					/>
				</DropOverActionList>
			)}
		</>
	);
};
MoreToolbarActions.displayName = "MoreToolbarActions";

type DropOverContentViewProps = {
	canSelectImage: boolean;
	canAddPoll: boolean;
	canAddLinkedDocuments: boolean;
	onSelectImages: (source: PostComposerSelectImageSourceKind) => void;
	onAddPoll: () => void;
	onAddLinkedDocument: (() => Promise<void>) | null;
};

const DropOverContentView = (props: DropOverContentViewProps): ReactElement => {
	const {
		canSelectImage,
		canAddPoll,
		canAddLinkedDocuments,
		onSelectImages,
		onAddPoll,
		onAddLinkedDocument
	} = props;

	const [dropOverView, setDropOverView] = useState<DropOverView>("default");

	switch (dropOverView) {
		case "default":
			return (
				<>
					<ActionListItem
						trackingId="upload-image"
						text={localize("_Image")}
						icon="image"
						onClick={() => setDropOverView("select-image-source")}
						isDisabled={!canSelectImage}
						rightIcon="chevron-right"
						shouldNotAutoCloseContainingDropOver
					/>
					{onAddLinkedDocument && (
						<ActionListItem
							trackingId="add-attachment"
							text={localize("_Attachment")}
							icon="paperclip"
							onClick={onAddLinkedDocument}
							isDisabled={!canAddLinkedDocuments}
						/>
					)}
					<ActionListItem
						trackingId="add-poll"
						text={localize("_Poll")}
						icon="bars-progress"
						onClick={onAddPoll}
						isDisabled={!canAddPoll}
					/>
				</>
			);
		case "select-image-source":
			return (
				<ImageSelector
					onBack={() => setDropOverView("default")}
					onSelectImages={onSelectImages}
				/>
		);
		default:
			never(dropOverView);
	}
};
DropOverContentView.displayName = "DropOverContentView";

type DropOverView = "default" | "select-image-source";

type ImageSelectorProps = {
	onSelectImages: (source: PostComposerSelectImageSourceKind) => void;
	onBack: () => void;
};

const ImageSelector = (props: ImageSelectorProps): ReactElement => {
	const {
		onSelectImages,
		onBack
	} = props;

	return (
		<>
			<ActionListSubMenuHeader
				text={localize("_SelectImage")}
				onGoBack={onBack}
			/>
			<ActionListItem
				icon="folder-open"
				text={localize("_Workspace")}
				trackingId="select-from-workspace"
				onClick={() => onSelectImages("workspace")}
			/>
			<ActionListItem
				icon="upload"
				text={localize("_Device")}
				trackingId="select-from-device"
				onClick={() => onSelectImages("device")}
			/>
		</>
	);
};
ImageSelector.displayName = "ImageSelector";

CommunityPostComposerFooter.Toolbar = CommunityPostComposerFooterToolbar;

function getInitialPollData(): CommunityPollFormData {
	return {
		question: "",
		answers: [
			setClientId({ text: "" }),
			setClientId({ text: "" })
		],
		isMultiSelect: false
	};
}
